import { useState } from "react";
import { FiLock, FiMinus, FiPlus } from "react-icons/fi";
import {
  type AnimSetting,
  CATALOG_COLUMNS_MAX,
  CATALOG_FONT_SCALE_MAX,
  CATALOG_FONT_SCALE_MIN,
  CATALOG_HOVER_SCALE_MAX,
  CATALOG_HOVER_SCALE_MIN,
  CATALOG_LAYOUT_MIN,
  CATALOG_PAGE_LIMIT_MAX,
  CATALOG_ROWS_MAX,
  CATALOG_TEXT_LINES_MAX,
  CATALOG_TEXT_LINES_MIN,
  type CatalogImageSize,
  type ThreadMenuOpenMethod,
  useCatalogStore,
} from "@/features/catalog/stores";
import { useFavStore } from "@/features/fav-filter/stores/favStore";
import { useHistoryStore } from "@/features/history/stores";
import { usePostedHistoryStore } from "@/features/history/stores/postedHistoryStore";
import { useNgStore } from "@/features/ng-filter/stores";
import { clearAllUserData } from "@/features/settings/components/sections/clearAllUserData";
import { useSettingsStore } from "@/features/settings/hooks";
import {
  type DisplayDensity,
  GLOBAL_FONT_SCALE_MAX,
  GLOBAL_FONT_SCALE_MIN,
  type NewRepliesBannerPosition,
  type NewRepliesBannerWidth,
  POST_BODY_FONT_SIZE_MAX,
  POST_BODY_FONT_SIZE_MIN,
  THREAD_IMAGE_SCALE_OPTIONS,
  type ThreadImageScale,
} from "@/features/settings/stores";
import {
  type DarkModeSetting,
  darkModeToThemeSelectValue,
  themeSelectValueToDarkMode,
} from "@/features/settings/stores/legacyDisplaySettings";
import { useMixStore } from "@/features/settings/stores/mixStore";
import {
  NumberSettingInput,
  Select,
  SettingRow,
  SettingSection,
} from "@/features/settings/ui";
import { AI_IMAGE_METADATA_BOARD_SLUG } from "@/features/settings/utils/aiImageMetadataSetting";
import { useIsDesktop } from "@/shared/hooks";
import { useForceDisplayMode } from "@/shared/hooks/useMediaQuery";
import {
  createForceDisplayModeCookies,
  type ForceDisplayMode,
} from "@/shared/lib/forcePc";
import { Button, Input, Toggle } from "@/shared/ui/form";
import { ConfirmDialog } from "@/shared/ui/overlay";

interface DisplaySettingsProps {
  currentUrlSlug: string | null;
  category?: "display" | "catalog" | "thread";
}

interface CatalogLayoutInputProps {
  label: string;
  value: number;
  onChange: (value: number) => void;
  max?: number;
}

export const CatalogLayoutInput: React.FunctionComponent<
  CatalogLayoutInputProps
> = ({
  label,
  value,
  onChange,
  max = CATALOG_COLUMNS_MAX,
}: CatalogLayoutInputProps) => {
  return (
    <NumberSettingInput
      min={CATALOG_LAYOUT_MIN}
      max={max}
      step={1}
      value={value}
      onCommit={onChange}
      aria-label={label}
    />
  );
};

interface ThreadMenuOpenMethodSelectProps {
  value: ThreadMenuOpenMethod;
  onChange: (value: ThreadMenuOpenMethod) => void;
}

export const ThreadMenuOpenMethodSelect: React.FunctionComponent<
  ThreadMenuOpenMethodSelectProps
> = ({ value, onChange }: ThreadMenuOpenMethodSelectProps) => {
  return (
    <Select
      value={value}
      onChange={(nextValue: string): void =>
        onChange(nextValue as ThreadMenuOpenMethod)
      }
      aria-label="メニューの開き方"
    >
      <option value="auto">自動</option>
      <option value="long-press">長押し</option>
      <option value="menu-button">メニューボタン</option>
      <option value="off">オフ</option>
    </Select>
  );
};

interface ThemeSelectProps {
  value: DarkModeSetting;
  onChange: (value: DarkModeSetting) => void;
}

const ThemeSelect: React.FunctionComponent<ThemeSelectProps> = ({
  value,
  onChange,
}: ThemeSelectProps) => {
  return (
    <Select
      value={darkModeToThemeSelectValue(value)}
      onChange={(nextValue: string): void =>
        onChange(themeSelectValueToDarkMode(nextValue))
      }
      aria-label="テーマ切替"
    >
      <option value="dark">ダーク</option>
      <option value="light">ライト</option>
      <option value="system">システムに合わせる</option>
    </Select>
  );
};

interface DisplayModeSelectProps {
  value: ForceDisplayMode;
  onChange: (value: ForceDisplayMode) => void;
}

const DisplayModeSelect: React.FunctionComponent<DisplayModeSelectProps> = ({
  value,
  onChange,
}: DisplayModeSelectProps) => {
  return (
    <Select
      value={value}
      onChange={(nextValue: string): void =>
        onChange(nextValue as ForceDisplayMode)
      }
      aria-label="表示モード"
    >
      <option value="auto">自動</option>
      <option value="pc">常にPC版</option>
      <option value="mobile">常にSP版</option>
    </Select>
  );
};

/**
 * 表示設定タブのコンテンツ
 */
export const DisplaySettings: React.FunctionComponent<DisplaySettingsProps> = ({
  currentUrlSlug,
  category = "display",
}: DisplaySettingsProps) => {
  const {
    darkMode,
    deleteKey,
    setDeleteKey,
    privacyMode,
    setDarkMode,
    setPrivacyMode,
    globalFontScale,
    setGlobalFontScale,
    spaceMode,
    setSpaceMode,
    jukeboxEnabled,
    setJukeboxEnabled,
    showR18,
    setShowR18,
    resetSettings,
    postBodyFontSize,
    setPostBodyFontSize,
    ogpPreviewEnabled,
    setOgpPreviewEnabled,
    externalEmbedEnabled,
    setExternalEmbedEnabled,
    imageClickOpensNewTab,
    setImageClickOpensNewTab,
    aiImageMetadataEnabled,
    setAiImageMetadataEnabled,
    threadAiSummaryEnabled,
    setThreadAiSummaryEnabled,
    fontFamily,
    setFontFamily,
    displayDensity,
    setDisplayDensity,
    threadImageScale,
    setThreadImageScale,
    headerFixed,
    setHeaderFixed,
    extendedTextEffects,
    setExtendedTextEffects,
    wheelReloadEnabled,
    setWheelReloadEnabled,
    replyButtonFixed,
    setReplyButtonFixed,
    newRepliesBannerWidth,
    setNewRepliesBannerWidth,
    newRepliesBannerPosition,
    setNewRepliesBannerPosition,
  } = useSettingsStore();
  const {
    columns,
    rows,
    textLines,
    imageSize,
    openInNewTab,
    showNew,
    showCount,
    showUnreadCount,
    catalogAnim,
    autoReload,
    catalogHoverPreview,
    catalogHoverScale,
    catalogFontScale,
    setCatalogFontScale,
    threadMenuOpenMethod,
    setColumns,
    setRows,
    setTextLines,
    setImageSize,
    setOpenInNewTab,
    setShowNew,
    setShowCount,
    setShowUnreadCount,
    setCatalogAnim,
    setAutoReload,
    setCatalogHoverPreview,
    setCatalogHoverScale,
    setThreadMenuOpenMethod,
    resetCatalogSettings,
  } = useCatalogStore();
  const { clearHistory } = useHistoryStore();
  const resetMixSettings = useMixStore((state) => state.resetBoardSettings);
  const clearPostedHistory = usePostedHistoryStore(
    (state) => state.clearPostedHistory,
  );
  const { clearAllNgSettings } = useNgStore();
  const { clearAllFavSettings } = useFavStore();
  const forceDisplayMode = useForceDisplayMode();
  const isDesktop = useIsDesktop();

  const [showClearHistoryDialog, setShowClearHistoryDialog] = useState(false);
  const [showClearAllDataDialog, setShowClearAllDataDialog] = useState(false);

  const handleClearHistory = (): void => {
    clearHistory();
    clearPostedHistory();
  };

  const handleForceDisplayModeChange = (mode: ForceDisplayMode): void => {
    if (mode === forceDisplayMode) return;

    const action =
      mode === "pc" ? "set-pc" : mode === "mobile" ? "set-mobile" : "clear";
    for (const cookie of createForceDisplayModeCookies(action)) {
      // biome-ignore lint/suspicious/noDocumentCookie: 既存のforcePc.ts Cookie機構(URLクエリ経由と同じ書式)を設定画面からも直接書き込むため
      document.cookie = cookie;
    }
    // pointer/幅の自動判定と強制指定はページ読み込み時に一度だけ解決されるため、
    // 反映にはリロードが必要(PC版に切替/スマホ版に戻るリンクと同じ挙動)。
    window.location.reload();
  };

  const handleClearAllData = (): void => {
    clearAllUserData({
      clearHistory,
      clearPostedHistory,
      clearAllNgSettings,
      clearAllFavSettings,
      resetCatalogSettings,
      resetSettings,
      resetMixSettings,
    });
  };

  const replyButtonFixedSetting = isDesktop && (
    <SettingRow
      label="レス送信ボタンを固定"
      description="レス送信ボタンを画面右下に固定します。OFFではスレッド末尾に表示します"
    >
      <Toggle
        checked={replyButtonFixed}
        onChange={setReplyButtonFixed}
        aria-label="レス送信ボタン固定切替"
      />
    </SettingRow>
  );

  return (
    <>
      {category === "display" && (
        <SettingSection title="全体設定">
          <SettingRow label="テーマ">
            <ThemeSelect value={darkMode} onChange={setDarkMode} />
          </SettingRow>
          <SettingRow
            label="表示モード"
            description="表示モードを自動判定するか、PC版/SP版に固定します。変更するとページが再読み込みされます"
          >
            <DisplayModeSelect
              value={forceDisplayMode}
              onChange={handleForceDisplayModeChange}
            />
          </SettingRow>
          <SettingRow
            label={
              <span className="flex items-center gap-2">
                <FiLock aria-hidden="true" size={16} />
                プライバシーモード
              </span>
            }
            description="画像を薄暗くして周囲から見えにくくします"
          >
            <Toggle
              checked={privacyMode}
              onChange={setPrivacyMode}
              aria-label="プライバシーモード切替"
            />
          </SettingRow>
          <SettingRow
            label="全体文字サイズ"
            description={`${globalFontScale}%`}
          >
            <fieldset className="m-0 flex min-w-0 flex-1 items-center gap-2 border-0 p-0">
              <legend className="sr-only">全体文字サイズ</legend>
              <button
                type="button"
                onClick={(): void => setGlobalFontScale(globalFontScale - 5)}
                disabled={globalFontScale <= GLOBAL_FONT_SCALE_MIN}
                className="flex items-center justify-center w-8 h-8 rounded-md bg-card border border-border text-foreground hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="全体文字サイズを小さく"
              >
                <FiMinus size={16} aria-hidden="true" />
              </button>
              <input
                type="range"
                min={GLOBAL_FONT_SCALE_MIN}
                max={GLOBAL_FONT_SCALE_MAX}
                step={5}
                value={globalFontScale}
                onChange={(e: React.ChangeEvent<HTMLInputElement>): void =>
                  setGlobalFontScale(Number(e.target.value))
                }
                className="flex-1"
                aria-label="全体文字サイズ"
              />
              <button
                type="button"
                onClick={(): void => setGlobalFontScale(globalFontScale + 5)}
                disabled={globalFontScale >= GLOBAL_FONT_SCALE_MAX}
                className="flex items-center justify-center w-8 h-8 rounded-md bg-card border border-border text-foreground hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="全体文字サイズを大きく"
              >
                <FiPlus size={16} aria-hidden="true" />
              </button>
            </fieldset>
          </SettingRow>
          {isDesktop && (
            <SettingRow
              label="表示密度"
              description="ヘッダー・レス間・投稿フォームの余白をまとめて詰めます（既定は標準）"
            >
              <Select
                value={displayDensity}
                onChange={(value: string): void =>
                  setDisplayDensity(value as DisplayDensity)
                }
                aria-label="表示密度"
              >
                <option value="comfortable">標準</option>
                <option value="compact">コンパクト</option>
              </Select>
            </SettingRow>
          )}
          {isDesktop && (
            <SettingRow
              label="ヘッダー固定"
              description="共通ヘッダーからページツールバーまでを画面上部に固定します"
            >
              <Toggle
                checked={headerFixed}
                onChange={setHeaderFixed}
                aria-label="ヘッダー固定切替"
              />
            </SettingRow>
          )}
          <SettingRow
            label="ホイール操作で更新"
            description="画面端で同じ方向へホイール操作を続けるとカタログまたはスレッドを更新します"
          >
            <Toggle
              checked={wheelReloadEnabled}
              onChange={setWheelReloadEnabled}
              aria-label="ホイール更新切替"
            />
          </SettingRow>
          {isDesktop && (
            <SettingRow label="新着レス通知の横幅">
              <Select
                value={newRepliesBannerWidth}
                onChange={(value: string): void =>
                  setNewRepliesBannerWidth(value as NewRepliesBannerWidth)
                }
                aria-label="新着レス通知の横幅"
              >
                <option value="default">標準</option>
                <option value="compact">コンパクト</option>
                <option value="full">画面いっぱい</option>
              </Select>
            </SettingRow>
          )}
          {isDesktop && (
            <SettingRow label="新着レス通知の位置">
              <Select
                value={newRepliesBannerPosition}
                onChange={(value: string): void =>
                  setNewRepliesBannerPosition(value as NewRepliesBannerPosition)
                }
                disabled={newRepliesBannerWidth === "full"}
                aria-label="新着レス通知の位置"
              >
                <option value="default">標準</option>
                <option value="left">左端</option>
                <option value="center-left">中央やや左</option>
                <option value="center">中央</option>
                <option value="center-right">中央やや右</option>
                <option value="right">右端</option>
              </Select>
            </SettingRow>
          )}
          <SettingRow
            label="ジュークボックス"
            description="メニューにジュークボックスを表示し、みんなで共有する音楽を再生できるようにします。"
          >
            <Toggle
              checked={jukeboxEnabled}
              onChange={setJukeboxEnabled}
              aria-label="ジュークボックス切替"
            />
          </SettingRow>
          <SettingRow
            label="R18画像を表示"
            description="R18スレッドの画像をぼかさず表示します"
          >
            <Toggle
              checked={showR18}
              onChange={setShowR18}
              aria-label="R18画像表示切替"
            />
          </SettingRow>
          <SettingRow label="書き込み認証">
            <p className="text-sm text-muted-foreground">
              投稿フォームに表示される公式ALTCHAウィジェットで確認します
            </p>
          </SettingRow>
        </SettingSection>
      )}
      {category === "thread" && (
        <SettingSection title="スレッド設定">
          <SettingRow
            label="本文文字サイズ"
            description={`${postBodyFontSize}px`}
          >
            <fieldset className="m-0 flex min-w-0 flex-1 items-center gap-2 border-0 p-0">
              <legend className="sr-only">本文文字サイズ</legend>
              <button
                type="button"
                onClick={(): void => setPostBodyFontSize(postBodyFontSize - 1)}
                disabled={postBodyFontSize <= POST_BODY_FONT_SIZE_MIN}
                className="flex items-center justify-center w-8 h-8 rounded-md bg-card border border-border text-foreground hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="本文文字サイズを小さく"
              >
                <FiMinus size={16} aria-hidden="true" />
              </button>
              <input
                type="range"
                min={POST_BODY_FONT_SIZE_MIN}
                max={POST_BODY_FONT_SIZE_MAX}
                step={1}
                value={postBodyFontSize}
                onChange={(e: React.ChangeEvent<HTMLInputElement>): void =>
                  setPostBodyFontSize(Number(e.target.value))
                }
                className="flex-1"
                aria-label="本文文字サイズ"
              />
              <button
                type="button"
                onClick={(): void => setPostBodyFontSize(postBodyFontSize + 1)}
                disabled={postBodyFontSize >= POST_BODY_FONT_SIZE_MAX}
                className="flex items-center justify-center w-8 h-8 rounded-md bg-card border border-border text-foreground hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="本文文字サイズを大きく"
              >
                <FiPlus size={16} aria-hidden="true" />
              </button>
            </fieldset>
          </SettingRow>
          <SettingRow
            label="OGPプレビュー"
            description="投稿内URLのリンクカードを表示します（既定ON）"
          >
            <Toggle
              checked={ogpPreviewEnabled}
              onChange={setOgpPreviewEnabled}
              aria-label="OGPプレビュー切替"
            />
          </SettingRow>
          <SettingRow
            label="AI要約"
            description="スレッド内のAI要約を表示します（既定ON）"
          >
            <Toggle
              checked={threadAiSummaryEnabled}
              onChange={setThreadAiSummaryEnabled}
              aria-label="AI要約表示切替"
            />
          </SettingRow>
          <SettingRow
            label="外部埋め込みを表示"
            description="投稿内URLのYouTube等をiframeで再生します（既定OFF・リンクのみ表示）"
          >
            <Toggle
              checked={externalEmbedEnabled}
              onChange={setExternalEmbedEnabled}
              aria-label="外部埋め込み表示切替"
            />
          </SettingRow>
          <SettingRow
            label="画像クリック時に別タブで開く"
            description="画像ビューアーを使わず、元画像を別タブで開きます"
          >
            <Toggle
              checked={imageClickOpensNewTab}
              onChange={setImageClickOpensNewTab}
              aria-label="画像クリック時に別タブで開く切替"
            />
          </SettingRow>
          <SettingRow
            label="本文フォント"
            description="レス本文に使うフォントを指定します。空欄なら既定のフォントのままです"
          >
            <Input
              type="text"
              value={fontFamily}
              placeholder="例: Meiryo"
              onChange={(event: React.ChangeEvent<HTMLInputElement>): void =>
                setFontFamily(event.target.value)
              }
              aria-label="本文フォント"
            />
          </SettingRow>
          {isDesktop && (
            <SettingRow
              label="スレッド画像の拡大率"
              description="PC版で拡大ボタンを押したときの原寸に対する表示倍率です（既定100%）"
            >
              <Select
                value={String(threadImageScale)}
                onChange={(value: string): void =>
                  setThreadImageScale(Number(value) as ThreadImageScale)
                }
                aria-label="スレッド画像の拡大率"
              >
                {THREAD_IMAGE_SCALE_OPTIONS.map((scale) => (
                  <option key={scale} value={scale}>
                    {scale}%
                  </option>
                ))}
              </Select>
            </SettingRow>
          )}
          {replyButtonFixedSetting}
          {!isDesktop && (
            <SettingRow label="宇宙モード">
              <Toggle
                checked={spaceMode}
                onChange={setSpaceMode}
                aria-label="宇宙モード切替"
              />
            </SettingRow>
          )}
          <SettingRow
            label="定型文の発光を拡張"
            description="引用行・カタログと追加の定型文にも発光や色効果を適用します"
          >
            <Toggle
              checked={extendedTextEffects}
              onChange={setExtendedTextEffects}
              aria-label="定型文の発光拡張切替"
            />
          </SettingRow>
          {(currentUrlSlug === null ||
            currentUrlSlug === AI_IMAGE_METADATA_BOARD_SLUG) && (
            <SettingRow
              label="AI画像メタデータ"
              description="画像のメタデータの読取・表示を有効にします（AI裏限定/既定ON）"
            >
              <Toggle
                checked={aiImageMetadataEnabled}
                onChange={setAiImageMetadataEnabled}
                aria-label="AI画像メタデータ表示切替"
              />
            </SettingRow>
          )}
        </SettingSection>
      )}
      {category === "catalog" && (
        <SettingSection title="カタログ設定">
          <SettingRow
            label="アニメ画像を再生"
            description="GIF/WebP等のカタログでの再生方法です"
          >
            <Select
              value={catalogAnim}
              onChange={(value: string): void =>
                setCatalogAnim(value as AnimSetting)
              }
              aria-label="アニメ画像"
            >
              <option value="always">常に動く</option>
              <option value="hover">カーソル時のみ</option>
              <option value="never">動かない</option>
            </Select>
          </SettingRow>
          <SettingRow
            label="カタログ列数"
            description={`設定可能範囲 ${CATALOG_LAYOUT_MIN}～${CATALOG_COLUMNS_MAX}`}
          >
            <CatalogLayoutInput
              label="カタログ列数"
              value={columns}
              onChange={setColumns}
              max={CATALOG_COLUMNS_MAX}
            />
          </SettingRow>
          <SettingRow
            label="カタログ行数"
            description={`設定可能範囲 ${CATALOG_LAYOUT_MIN}～${CATALOG_ROWS_MAX}（${Math.min(CATALOG_PAGE_LIMIT_MAX, columns * rows)}件ずつ取得します）`}
          >
            <CatalogLayoutInput
              label="カタログ行数"
              value={rows}
              onChange={setRows}
              max={CATALOG_ROWS_MAX}
            />
          </SettingRow>
          <SettingRow
            label="スレッド本文の表示行数"
            description="設定可能範囲 0～3（0で非表示・既定1行）"
          >
            <NumberSettingInput
              min={CATALOG_TEXT_LINES_MIN}
              max={CATALOG_TEXT_LINES_MAX}
              step={1}
              value={textLines}
              onCommit={setTextLines}
              aria-label="スレッド本文の表示行数"
            />
          </SettingRow>
          <SettingRow label="画像サイズ">
            <Select
              value={imageSize}
              onChange={(value: string): void =>
                setImageSize(Number(value) as CatalogImageSize)
              }
              aria-label="カタログ画像サイズ"
            >
              {[50, 75, 100, 125, 150, 175, 200].map((size) => (
                <option key={size} value={size}>
                  {size}px
                </option>
              ))}
            </Select>
          </SettingRow>
          <SettingRow label="新規タブで開く">
            <Toggle
              checked={openInNewTab}
              onChange={setOpenInNewTab}
              aria-label="スレッドを新規タブで開く"
            />
          </SettingRow>
          <SettingRow
            label="カタログ自動更新"
            description="既定OFF。表示中は60秒、非表示中は5分間隔でカタログを更新し、非表示中の定期更新ではオンライン人数を更新しません"
          >
            <Toggle
              checked={autoReload}
              onChange={setAutoReload}
              aria-label="カタログ自動更新"
            />
          </SettingRow>
          {/* ホバープレビューはhoverのあるPC版専用(タッチ環境では発火しない) */}
          {isDesktop && (
            <>
              <SettingRow
                label="ホバープレビュー"
                description="カタログ画像にカーソルを合わせるとスレ画の拡大とスレ文を表示します（既定OFF）"
              >
                <Toggle
                  checked={catalogHoverPreview}
                  onChange={setCatalogHoverPreview}
                  aria-label="カタログのホバープレビュー切替"
                />
              </SettingRow>
              <SettingRow
                label="プレビューの拡大率"
                description={`${CATALOG_HOVER_SCALE_MIN}〜${CATALOG_HOVER_SCALE_MAX}%で指定します`}
              >
                <NumberSettingInput
                  min={CATALOG_HOVER_SCALE_MIN}
                  max={CATALOG_HOVER_SCALE_MAX}
                  step={25}
                  value={catalogHoverScale}
                  disabled={!catalogHoverPreview}
                  onCommit={setCatalogHoverScale}
                  aria-label="ホバープレビューの拡大率"
                />
              </SettingRow>
            </>
          )}
          <SettingRow
            label="文字サイズ"
            description={`${CATALOG_FONT_SCALE_MIN}〜${CATALOG_FONT_SCALE_MAX}%で指定します`}
          >
            <NumberSettingInput
              min={CATALOG_FONT_SCALE_MIN}
              max={CATALOG_FONT_SCALE_MAX}
              step={10}
              value={catalogFontScale}
              onCommit={setCatalogFontScale}
              aria-label="カタログの文字サイズ"
            />
          </SettingRow>
          <SettingRow label="NEW表示">
            <Toggle
              checked={showNew}
              onChange={setShowNew}
              aria-label="NEW表示切替"
            />
          </SettingRow>
          <SettingRow label="レス数表示">
            <Toggle
              checked={showCount}
              onChange={setShowCount}
              aria-label="レス数表示切替"
            />
          </SettingRow>
          <SettingRow label="未読レス数表示">
            <Toggle
              checked={showUnreadCount}
              onChange={setShowUnreadCount}
              aria-label="未読レス数表示切替"
            />
          </SettingRow>
          {/* メニューの開き方(長押し等)はモバイル版CatalogItem専用の設定で、
        PC版のDesktopCatalogItemは参照しないため非表示(#34) */}
          {!isDesktop && (
            <SettingRow label="メニューの開き方">
              <ThreadMenuOpenMethodSelect
                value={threadMenuOpenMethod}
                onChange={setThreadMenuOpenMethod}
              />
            </SettingRow>
          )}
        </SettingSection>
      )}
      {category === "display" && (
        <>
          <SettingSection title="投稿設定">
            <SettingRow
              label="削除キー"
              description="投稿削除時に使用するパスワードです"
            >
              <Input
                type="text"
                value={deleteKey}
                onChange={(
                  e: React.ChangeEvent<HTMLInputElement, HTMLInputElement>,
                ): void => setDeleteKey(e.target.value)}
              />
            </SettingRow>
          </SettingSection>
          <SettingSection title="データ管理">
            <SettingRow>
              <Button
                variant="default"
                className="w-full py-3"
                onClick={(): void => setShowClearHistoryDialog(true)}
              >
                履歴をクリア
              </Button>
            </SettingRow>
            <SettingRow>
              <Button
                variant="destructive"
                className="w-full py-3"
                onClick={(): void => setShowClearAllDataDialog(true)}
              >
                全データをクリア
              </Button>
            </SettingRow>
          </SettingSection>

          <ConfirmDialog
            isOpen={showClearHistoryDialog}
            onClose={(): void => setShowClearHistoryDialog(false)}
            onConfirm={handleClearHistory}
            title="履歴をクリア"
            message="スレッド閲覧履歴をすべて削除します。この操作は取り消せません。"
            confirmText="削除"
            variant="destructive"
          />

          <ConfirmDialog
            isOpen={showClearAllDataDialog}
            onClose={(): void => setShowClearAllDataDialog(false)}
            onConfirm={handleClearAllData}
            title="全データをクリア"
            message={`以下のすべてのデータを削除します。この操作は取り消せません。\n\n・スレッド閲覧履歴\n・NG設定（非表示スレッド、NGワード等）\n・お気に入り設定（お気に入りワード等）\n・カタログ設定（ソート、列数、表示設定等）\n・その他の設定（テーマ、プライバシーモード等）`}
            confirmText="削除"
            variant="destructive"
          />
        </>
      )}
    </>
  );
};
